'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { FileText, BarChart3, TrendingUp, Menu, X, Bell, ChevronDown, LogOut, Settings, User, Target,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navItems = [
  { label: 'Write Essay', href: '/essay-writing-interface', icon: FileText },
  { label: 'My Results', href: '/assessment-results', icon: BarChart3 },
  { label: 'Progress', href: '/progress', icon: TrendingUp },
];

const mockUser = {
  name: 'Priya Nair',
  email: 'priya.nair@gmail.com',
  examType: 'Academic',
  targetBand: 7.5,
  examDate: '14 Mar 2026',
};

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-card" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0">
            <AppLogo size={34} />
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-base tracking-tight text-foreground">
                IELTSCoach
              </span>
              <span className="text-xs font-medium text-muted-foreground leading-none">
                AI Examiner
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems?.map((item) => {
              const Icon = item?.icon;
              const isActive = pathname === item?.href;
              return (
                <Link
                  key={`nav-${item?.href}`}
                  href={item?.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-primary/10 text-primary' :'text-secondary-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon size={16} />
                  {item?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Target Band Pill */}
            <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/8 border border-primary/20">
              <Target size={13} className="text-primary" />
              <span className="text-xs font-semibold text-primary">
                Target: Band {mockUser?.targetBand}
              </span>
            </div>

            {/* Notifications */}
            <button className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-danger" />
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
              >
                <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">
                    {mockUser?.name?.split(' ')?.map((n) => n?.[0])?.join('')}
                  </span>
                </div>
                <span className="hidden lg:block text-sm font-semibold text-foreground">
                  {mockUser?.name?.split(' ')?.[0]}
                </span>
                <ChevronDown size={14} className="text-muted-foreground" />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 bg-card rounded-xl border shadow-lg z-50 fade-in overflow-hidden"
                >
                  <div className="px-4 py-3 border-b bg-muted/40">
                    <p className="text-sm font-semibold text-foreground">{mockUser?.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{mockUser?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        {mockUser?.examType}
                      </span>
                      <span className="text-xs text-muted-foreground">Exam: {mockUser?.examDate}</span>
                    </div>
                  </div>
                  <div className="py-1">
                    {[
                      { icon: User, label: 'My Profile' },
                      { icon: Settings, label: 'Settings' },
                    ]?.map((item) => {
                      const Icon = item?.icon;
                      return (
                        <button
                          key={`menu-${item?.label}`}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-foreground hover:bg-muted transition-all duration-150"
                        >
                          <Icon size={15} />
                          {item?.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="border-t py-1">
                    <Link
                      href="/sign-up-login"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger-bg transition-all duration-150"
                    >
                      <LogOut size={15} />
                      Sign Out
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:bg-muted transition-all duration-150"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-card fade-in">
          <div className="px-4 py-3 space-y-1">
            {navItems?.map((item) => {
              const Icon = item?.icon;
              const isActive = pathname === item?.href;
              return (
                <Link
                  key={`mobile-nav-${item?.href}`}
                  href={item?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-primary/10 text-primary' :'text-secondary-foreground hover:bg-muted'
                  }`}
                >
                  <Icon size={16} />
                  {item?.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}